/** @format */

import React, { useEffect, useState } from "react";
import { Modal, Button, FormGroup, Input, Label } from "reactstrap";
import { FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import http from "../../services/http";
import { getVendorSettings, setVendorData } from "../../services/helper";

const POSModal = ({
  isOpen,
  toggle,
  products,
  handleSelectPOSProduct,
  selectedProduct,
  setSelectedProduct,
  handleSave,
}) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [vendorPrice, setVendorPrice] = useState("");

  useEffect(() => {
    if (selectedProduct?.shortcut_key) {
      setSelectedOption(selectedProduct.shortcut_key);
    } else {
      setSelectedOption("");
    }
    if (selectedProduct) {
      const overrides = getVendorSettings("pos_price_overrides") || {};
      setVendorPrice(overrides[selectedProduct.id] ?? "");
    }
  }, [selectedProduct]);

  const handleShortcutChange = (e) => {
    const key = e.target.value;
    setSelectedOption(key);

    if (!key || !selectedProduct?.id) return;

    const productId = selectedProduct.id.toString();

    // Load and parse stored shortcuts
    const stored = localStorage.getItem("shortcodeActions");
    let parsed = stored ? JSON.parse(stored) : {};

    // Remove existing keys assigned to this product
    for (const shortcut in parsed) {
      if (parsed[shortcut] === productId) {
        delete parsed[shortcut];
      }
    }

    // Assign new shortcut
    parsed[key.replace("+", "")] = productId;
    localStorage.setItem("shortcodeActions", JSON.stringify(parsed));

    // Update selected product state
    setSelectedProduct((prev) => ({
      ...prev,
      shortcut_key: key,
    }));
  };

  const handleSaveWithVendorPrice = async () => {
    if (vendorPrice !== "") {
      const overrides = getVendorSettings("pos_price_overrides") || {};
      overrides[selectedProduct.id] = parseFloat(vendorPrice);
      try {
        await http.saveVendorData({ pos_price_overrides: overrides });
        setVendorData({ pos_price_overrides: overrides });
        setSelectedProduct((prev) => ({
          ...prev,
          price: parseFloat(vendorPrice),
          regular_price: parseFloat(vendorPrice),
        }));
        toast.success("Vendor price saved");
      } catch (e) {
        toast.error("Failed to save vendor price");
      }
    }
    handleSave();
  };

  const handleClearVendorPrice = async () => {
    const overrides = getVendorSettings("pos_price_overrides") || {};
    delete overrides[selectedProduct.id];
    try {
      await http.saveVendorData({ pos_price_overrides: overrides });
      setVendorData({ pos_price_overrides: overrides });
      setVendorPrice("");
      const originalProduct = products.find((p) => p.id === selectedProduct.id);
      if (originalProduct) {
        setSelectedProduct((prev) => ({
          ...prev,
          price: originalProduct.regular_price,
          regular_price: originalProduct.regular_price,
        }));
      }
      toast.success("Vendor price removed, using default price");
      handleSave();
    } catch (e) {
      toast.error("Failed to remove vendor price");
    }
  };

  const renderShortcutDropdown = () => (
    <FormGroup>
      <Label for="shortcutKeys">Shortcode Keys</Label>
      <Input
        id="shortcutKeys"
        type="select"
        value={selectedOption}
        onChange={handleShortcutChange}
      >
        <option value="">Select Shortcode</option>
        {[...Array(10)].map((_, i) => (
          <option key={i} value={`${i}`}>
            {i}
          </option>
        ))}
        {[...Array(10)].map((_, i) => (
          <option key={i + 10} value={`Shift+${i}`}>
            Shift + {i}
          </option>
        ))}
      </Input>
    </FormGroup>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <div className="modal-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          {selectedProduct ? "Edit Product" : "Select Products for POS"}
        </h5>
        <Button className="close-btn" onClick={toggle}>
          <FiX size={20} />
        </Button>
      </div>

      <div className="modal-body">
        {!selectedProduct ? (
          <FormGroup>
            <Input
              type="select"
              onChange={(e) =>
                handleSelectPOSProduct(
                  products.find((p) => p.id == e.target.value) // do not change == to ===
                )
              }
            >
              <option value="">Select Product</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} - ${product.regular_price}
                </option>
              ))}
            </Input>
          </FormGroup>
        ) : (
          <>
            <FormGroup>
              <Label>Default Price: <strong>${selectedProduct.regular_price}</strong></Label>
            </FormGroup>

            <FormGroup>
              <Label>Vendor Price Override</Label>
              <div className="d-flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={vendorPrice}
                  onChange={(e) => setVendorPrice(e.target.value)}
                  placeholder={`Default: $${selectedProduct.regular_price}`}
                />
                {vendorPrice !== "" && (
                  <Button color="warning" size="sm" onClick={handleClearVendorPrice}>
                    Reset
                  </Button>
                )}
              </div>
              <small className="text-muted">Leave empty to use default price</small>
            </FormGroup>

            <FormGroup className="d-flex align-items-center">
              <Input
                type="checkbox"
                role="switch"
                checked={selectedProduct.taxable}
                onChange={(e) =>
                  setSelectedProduct((prev) => ({
                    ...prev,
                    taxable: e.target.checked,
                  }))
                }
              />
              <Label className="ms-2 mt-2">Taxable</Label>
            </FormGroup>
          </>
        )}
      </div>

      <div className="modal-footer">
        <Button color="danger" onClick={toggle}>
          Cancel
        </Button>
        {selectedProduct && (
          <Button color="primary" onClick={handleSaveWithVendorPrice}>
            Save Changes
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default POSModal;
